// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { CurrencyPairs } from '@shared/data-access/interfaces/api/brokerage/currency';
import type { ExchangeRatesState } from '@shared/data-access/interfaces/redux/assets-repository-state-types';
import type {
  QueryActionCreatorResult,
  QueryDefinition,
} from '@reduxjs/toolkit/query';

/**
 * Fetches exchange rates for a list of account positions and returns a mapping of
 * currency to exchange rate.
 *
 * @param positions - An array of account positions containing various currencies.
 * @param targetCurrency - The target currency to which exchange rates should be
 *   converted.
 * @param getCurrencyPrices - A function that takes an array of currency pairs and
 *   returns a promise that resolves to an array of currency data.
 * @returns A promise that resolves to an object mapping each currency to its
 *   exchange rate relative to the target currency.
 * @throws Will log an error message and return an empty object if fetching exchange
 *   rates fails.
 */
export const getExchangeRates = async (
  currencies: string[],
  targetCurrency: string,
  getCurrencyPrices: (
    currencies: string[],
  ) => QueryActionCreatorResult<
    QueryDefinition<CurrencyPairs, any, never, ExchangeRatesState[], any>
  >,
): Promise<{ [currency: string]: number }> => {
  try {
    const currencyPairs: CurrencyPairs = currencies.map(
      currency => currency + targetCurrency,
    );
    const data = await getCurrencyPrices(currencyPairs).unwrap();
    return data.reduce((acc: { [currency: string]: number }, { rate, pair }) => {
      const currency = pair.slice(0, 3);
      acc[currency] = rate;
      return acc;
    }, {} as { [currency: string]: number });
  } catch (error) {
    console.error('Error fetching exchange rates', error);
    return {};
  }
};
